import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { scores, Steps } from './data'
import { FeedbackCsatProps, FeedbackCsatUseControllerReturn } from './types'

let timeout: ReturnType<typeof setTimeout>

export const useController = ({
  onClose,
  onOpen,
  onSubmit,
  autoClose,
  autoCloseTimeout,
  open = true,
}: Omit<FeedbackCsatProps, 'texts'>): FeedbackCsatUseControllerReturn => {
  const [score, setScore] = useState(0)
  const [comment, setComment] = useState('')
  const [step, setStep] = useState<Steps>(Steps.Score)
  const [isOpen, setIsOpen] = useState(false)
  const wasSubmmited = useRef(false)

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const onSelectScore = (value: number): void => {
    setScore(value)
    setStep(Steps.Comment)
  }

  const onChangeComment = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const { value } = event.target
    setComment(value.trim())
  }

  const onReset = (): void => {
    setScore(0)
    setComment('')
    setStep(Steps.Score)
  }

  const onSubmitComment = (): void => {
    wasSubmmited.current = true
    setStep(Steps.Done)
    const scoreLabel = scores.find((s) => s.value === score)?.label ?? ''
    onSubmit?.({ score, comment, scoreLabel })
    if (!autoClose) {
      return
    }
    timeout = setTimeout(() => {
      setIsOpen(false)
      onClose?.()
      onReset()
    }, autoCloseTimeout)
  }

  const onOpenChange = (value: boolean): void => {
    clearTimeout(timeout)
    setIsOpen(value)
    if (value) {
      wasSubmmited.current = false
      onOpen?.()
      return
    }
    const scoreLabel = scores.find((s) => s.value === score)?.label ?? ''
    onClose?.(wasSubmmited.current ? undefined : { score, comment, scoreLabel })
    onReset()
  }

  const requiredComment = score < 4
  const commentTextKey = score > 3 ? 'positiveScore' : 'negativeScore'

  return {
    score,
    comment,
    step,
    isOpen,
    onSelectScore,
    onChangeComment,
    onSubmitComment,
    onOpenChange,
    requiredComment,
    commentTextKey,
  }
}
